import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import FileSaver from 'file-saver';
import { take } from 'rxjs/operators';

import { BillingService } from 'src/app/core/services/billing';
import { Invoice } from 'src/app/models/invoice.model';

@Component({
  selector: 'app-invoice-list',
  templateUrl: './invoice-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InvoiceListComponent {
  readonly invoices = input<Invoice[]>([]);
  readonly isInvoicesLoading = input<boolean>(false);
  readonly selectedInvoiceId = input<string | null>(null);

  readonly invoiceSelected = output<Invoice>();

  readonly viewingIds = signal(new Set<string>());
  readonly downloadingIds = signal(new Set<string>());

  private readonly billingService = inject(BillingService);
  private readonly destroyRef = inject(DestroyRef);

  getStatusClasses(status: string | null): Record<string, boolean> {
    switch (status?.toUpperCase()) {
      case 'DUE':     return { 'bg-red-50': true,   'text-red-600': true   };
      case 'PAID':    return { 'bg-green-100': true, 'text-green-700': true };
      case 'PENDING': return { 'bg-gray-100': true,  'text-gray-700': true  };
      default:        return { 'bg-blue-100': true,  'text-blue-700': true  };
    }
  }

  onView(invoice: Invoice): void {
    const versionId = invoice.file?.ContentDocument?.LatestPublishedVersionId;
    if (!versionId) return;

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      const id = invoice.Id;
      this.viewingIds.update((ids) => new Set([...ids, id]));

      this.billingService
        .getInvoiceBlobById(versionId)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({ url }) => {
            this.viewingIds.update((ids) => {
              const next = new Set(ids);
              next.delete(id);
              return next;
            });
            window.open(url, '_blank');
          },
          error: () => {
            this.viewingIds.update((ids) => {
              const next = new Set(ids);
              next.delete(id);
              return next;
            });
          },
        });
    } else {
      this.invoiceSelected.emit(invoice);
    }
  }

  onDownload(invoice: Invoice): void {
    const versionId = invoice.file?.ContentDocument?.LatestPublishedVersionId;
    if (!versionId) return;

    const id = invoice.Id;
    this.downloadingIds.update((ids) => new Set([...ids, id]));

    this.billingService
      .getInvoiceBlobById(versionId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ binaryData }) => {
          this.downloadingIds.update((ids) => {
            const next = new Set(ids);
            next.delete(id);
            return next;
          });
          const title = invoice.file!.ContentDocument.Title ?? invoice.Transaction_Date__c ?? 'invoice';
          FileSaver.saveAs(new Blob([binaryData], { type: 'application/pdf' }), `${title}.pdf`);
        },
        error: () => {
          this.downloadingIds.update((ids) => {
            const next = new Set(ids);
            next.delete(id);
            return next;
          });
        },
      });
  }
}
